<template>
    <div class="py-12">
        <h1
            class="text-3xl font-semibold leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.welcome") }}, {{ userProps.name }}!
        </h1>
    </div>

    <div class="mt-8 mb-8 grid gap-6 lg:grid-cols-2">
        <!-- Your Plan -->
        <div>
            <div class="mb-3 flex items-center justify-between">
                <h2 class="text-2xl ml-1 font-semibold text-gray-900">
                    Your Plan
                </h2>
                <Link
                    :href="route('billing.subscriptions.index')"
                    class="text-s font-medium text-blue-600 hover:text-blue-800"
                >
                    Manage Billing
                </Link>
            </div>

            <div
                class="flex flex-col gap-6 rounded-xl border border-gray-200 bg-white px-6 py-5 md:flex-row md:items-center h-[230px]"
            >
                <!-- Text block -->
                <div
                    class="flex-1 w-[170px] text-sm text-gray-700 flex flex-col justify-between h-full px-2 py-2"
                >
                    <p class="mb-4 text-[14px]">
                        {{
                            currentPaddleSubscription?.items[0]
                                ?.productDescription ||
                            "Explore all features for free before committing to a paid plan."
                        }}
                    </p>

                    <dl class="space-y-1 text-[14px]">
                        <div class="flex gap-3">
                            <!--PLAN-->
                            <dt class="w-32 text-gray-500">Plan</dt>
                            <dd
                                v-if="currentPaddleSubscription && isSubscribed"
                                class="font-medium text-gray-900"
                            >
                                {{
                                    currentPaddleSubscription?.items[0]?.productName
                                        ?.replace(/DMARC\/|eye\s*-\s*/g, "")
                                        .trim() || "Unknown Plan"
                                }}
                            </dd>
                            <dd
                                v-else-if="hasGodMode"
                                class="font-medium text-gray-900"
                            >
                                Enterprise Plan
                            </dd>
                            <dd
                                v-else-if="isTrialing && !isSubscribed"
                                class="font-medium text-gray-900"
                            >
                                TRIAL
                                {{
                                    isTrialing
                                        ? $t("vue.active")
                                        : $t("vue.expired")
                                }}
                            </dd>
                            <dd
                                v-else-if="!isTrialing && !isSubscribed"
                                class="font-medium text-gray-900"
                            >
                                TRIAL
                                {{
                                    isTrialing
                                        ? $t("vue.active")
                                        : $t("vue.expired")
                                }}
                            </dd>
                        </div>
                        <!--PAYMENT CYCLE-->
                        <div class="flex gap-3">
                            <dt v-if="isSubscribed" class="w-32 text-gray-500">
                                {{ $t("vue.payment_cycle") }}
                            </dt>
                            <dt
                                v-else-if="
                                    !isTrialing && !isSubscribed && trialEndsAt
                                "
                                class="w-32 text-gray-500"
                            >
                                {{ $t("vue.trial_expiration") }}
                            </dt>
                            <dt
                                v-else-if="isTrialing && trialEndsAt"
                                class="w-32 text-gray-500"
                            >
                                {{ $t("vue.trial_expiration") }}
                            </dt>
                            <div
                                v-if="isSubscribed"
                                class="font-medium text-gray-900"
                            >
                                {{
                                    !currentPaddleSubscription?.next_billing_at
                                        ? $t("vue.scheduled_to_be_canceled")
                                        : currentPaddleSubscription?.period ===
                                            "year"
                                          ? $t("vue.yearly")
                                          : $t("vue.monthly")
                                }}
                            </div>
                            <div
                                v-else-if="trialEndsAt"
                                class="font-medium text-gray-900"
                            >
                                {{ trialEndsAt }}
                            </div>
                        </div>
                        <div v-if="isSubscribed" class="flex gap-3">
                            <dt class="w-32 text-gray-500">
                                {{
                                    currentPaddleSubscription.change.action ===
                                    "cancel"
                                        ? $t("vue.scheduled_to_be_canceled_at")
                                        : $t("vue.next_invoice")
                                }}
                            </dt>
                            <dd class="font-medium text-gray-900">
                                {{
                                    currentPaddleSubscription?.next_billing_at
                                        ? new Date(
                                              currentPaddleSubscription
                                                  ?.next_billing_at.date,
                                          ).toLocaleDateString("en-US", {
                                              day: "numeric",
                                              month: "long",
                                              year: "numeric",
                                          })
                                        : currentPaddleSubscription
                                                ?.currentBillingPeriod?.start
                                          ? new Date(
                                                currentPaddleSubscription
                                                    ?.currentBillingPeriod.start
                                                    .date,
                                            ).toLocaleDateString("en-US", {
                                                day: "numeric",
                                                month: "long",
                                                year: "numeric",
                                            })
                                          : "N/A"
                                }}
                            </dd>
                        </div>
                        <div
                            v-if="currentPaddleSubscription && isSubscribed"
                            class="flex gap-3"
                        >
                            <dt class="w-32 text-gray-500">
                                {{ $t("vue.next_payment") }}
                            </dt>
                            <dd class="font-medium text-gray-900">
                                ${{ currentPaddleSubscription?.totals?.total }}
                            </dd>
                        </div>
                    </dl>
                </div>

                <!-- Slots card -->
                <div
                    class="flex w-full h-full max-w-xs flex-col items-center justify-center rounded-xl border border-gray-200 bg-gray-50 px-8 py-7"
                >
                    <p class="text-4xl font-semibold text-gray-900">
                        {{ domainSlots }}
                    </p>
                    <p
                        class="mt-1 text-xs font-semibold tracking-wide text-gray-500"
                    >
                        DOMAIN SLOTS
                    </p>

                    <AtomicButton
                        v-if="currentPaddleSubscription"
                        color="colorless"
                        class="mt-6 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        @click="visitCustomerPortal()"
                    >
                        {{ $t("vue.manage_subscription_invoices") }}
                    </AtomicButton>
                </div>
            </div>
        </div>

        <!-- Domain Overview -->
        <div>
            <div class="mb-3 flex items-center justify-between">
                <h2 class="text-2xl ml-1 font-semibold text-gray-900">
                    Domain Overview
                </h2>
                <Link
                    class="text-s font-medium text-blue-600 hover:text-blue-800"
                    :href="route('domains.index')"
                >
                    Manage domains
                </Link>
            </div>

            <div
                class="grid h-[230px] grid-cols-3 place-items-center rounded-xl border border-gray-200 bg-white px-10 py-9"
            >
                <div class="flex flex-col items-center text-center">
                    <p
                        class="text-5xl font-semibold leading-none text-gray-900"
                    >
                        {{ activeDomainsLength }}
                    </p>
                    <p
                        class="mt-4 text-xs font-semibold tracking-wide text-gray-500 uppercase"
                    >
                        {{ t("vue.active_domains") }}
                        <button
                            type="button"
                            class="info-tooltip left-1 top-1"
                            :data-tooltip="tt.activeDomains"
                            :aria-label="tt.activeDomains"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </p>
                </div>

                <div class="flex flex-col items-center text-center">
                    <p
                        class="text-5xl font-semibold leading-none text-gray-900 uppercase"
                    >
                        {{ inActiveDomainsLength }}
                    </p>
                    <p
                        class="mt-4 text-xs font-semibold tracking-wide text-gray-500"
                    >
                        {{ t("vue.inactive_domains") }}
                        <button
                            type="button"
                            class="info-tooltip left-1 top-1"
                            :data-tooltip="tt.inactiveDomains"
                            :aria-label="tt.inactiveDomains"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </p>
                </div>

                <div class="flex flex-col items-center text-center">
                    <p
                        class="text-5xl font-semibold leading-none text-gray-900 uppercase"
                    >
                        {{ emptySlots }}
                    </p>
                    <p
                        class="mt-4 text-xs font-semibold tracking-wide text-gray-500"
                    >
                        {{ t("vue.empty_slots") }}
                        <button
                            type="button"
                            class="info-tooltip left-1 top-1"
                            :data-tooltip="tt.emptySlots"
                            :aria-label="tt.emptySlots"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </p>
                </div>
            </div>
        </div>
    </div>

    <!-- ZERO DOMAINS VIEW STATE -->
    <div
        v-if="activeDomainsLength === 0"
        class="mb-50 flex min-h-[60vh] items-center justify-center text-center"
    >
        <div class="mt-6">
            <div class="flex flex-wrap justify-between">
                <div class="domains-none-list">
                    <img
                        :src="domainsNoneImgSrc"
                        alt="Domains none image"
                        class="domains-none-image"
                    />

                    <div class="domains-none-text">
                        <h2 class="domain-non-text-header">
                            {{ t("vue.none_active_domains_yet") }}
                        </h2>
                        <p class="domain-non-text-header-paragraph">
                            {{ t("vue.none_domains_yet_instruction") }}
                        </p>
                    </div>

                    <div class="domains-none-button">
                        <AtomicButton
                            color="yellow"
                            size="lg"
                            @click="redirectToDomains"
                        >
                            <template #icon>
                                <PlusCircleIcon class="mr-2 h-6 w-6" />
                            </template>
                            {{ t("vue.domains") }}
                        </AtomicButton>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!--  Overall Report Card  -->
    <div
        v-if="activeDomainsLength > 0"
        class="mb-3 flex items-center justify-between"
    >
        <h2 class="text-2xl ml-1 font-semibold leading-6 text-gray-900">
            {{ $t("vue.last_30_days") }}
        </h2>
        <Link
            class="text-s font-medium text-blue-600 hover:text-blue-800"
            :href="route('reports.index')"
        >
            See full reports
        </Link>
    </div>

    <div
        v-if="activeDomainsLength > 0"
        class="grid max-h-[189px] gap-5 mt-5 mb-5 pt-5 pb-5 md:grid-cols-4"
    >
        <ReportBox>
            <DonutChart :data="donutData" />
        </ReportBox>
        <ReportBox>
            <div
                class="flex h-[48px] w-[48px] items-center justify-center rounded-md bg-blue-500 p-2"
            >
                <Mail class="text-white"></Mail>
            </div>
            <div class="mt-8">
                <p class="text-sm font-medium text-gray-600">
                    Total Emails Reported
                </p>
                <p class="mt-1 text-4xl font-medium text-black">
                    {{ formatNumber(totalPass + totalFail) }}
                </p>
            </div>
        </ReportBox>
        <ReportBox>
            <div
                class="flex h-[48px] w-[48px] items-center justify-center rounded-md bg-green-500 p-2"
            >
                <Shield class="text-white"></Shield>
            </div>
            <div class="mt-8">
                <p class="text-sm font-medium text-gray-600">DMARC Compliant</p>
                <p class="mt-1 text-4xl font-medium text-black">
                    {{ formatNumber(totalPass) }}
                </p>
            </div>
        </ReportBox>
        <ReportBox>
            <div
                class="flex h-[48px] w-[48px] items-center justify-center rounded-md bg-red-500 p-2"
            >
                <ShieldAlertIcon class="text-white"></ShieldAlertIcon>
            </div>
            <div class="mt-8">
                <p class="text-sm font-medium text-gray-600">
                    DMARC Non-Compliant
                </p>
                <p class="mt-1 text-4xl font-medium text-black">
                    {{ formatNumber(totalFail) }}
                </p>
            </div>
        </ReportBox>
    </div>

    <!-- Most Active Domains Overview -->
    <div v-if="activeDomainsLength > 0">
        <div class="mb-3 flex items-center justify-between">
            <h2 class="text-2xl ml-1 font-semibold leading-6 text-gray-900">
                {{ $t("vue.domain_overview_dashboard") }}
            </h2>
            <Link
                class="text-s font-medium text-blue-600 hover:text-blue-800"
                :href="route('reports.index')"
            >
                See full reports
            </Link>
        </div>
        <div
            class="mt-10 overflow-hidden rounded-xl border border-gray-200 bg-white mb-80"
        >
            <div class="overflow-x-auto">
                <table class="min-w-full table-auto">
                    <thead>
                        <tr class="border-b border-gray-100">
                            <th
                                class="px-6 pt-6 pb-4 w-14 text-left text-[11px] font-semibold uppercase tracking-wide text-[#737373]"
                            >
                                {{ $t("vue.dashboard_table_domain") }}
                            </th>
                            <th
                                class="px-6 pt-6 pb-4 w-14 text-left text-[11px] font-semibold uppercase tracking-wide text-[#737373]"
                            >
                                {{ $t("vue.dashboard_table_current_policy") }}
                            </th>
                            <th
                                class="px-6 pt-6 pb-4 w-14 text-left text-[11px] font-semibold uppercase tracking-wide text-[#737373]"
                            >
                                {{ $t("vue.dashboard_table_emails_reported") }}
                            </th>
                            <th
                                class="px-6 pt-6 pb-4 w-14 text-left text-[11px] font-semibold uppercase tracking-wide text-[#737373]"
                            >
                                {{ $t("vue.dashboard_table_dmarc_compliance") }}
                            </th>
                            <th
                                class="px-6 pt-6 pb-4 w-14 text-left text-[11px] font-semibold uppercase tracking-wide text-[#737373]"
                            ></th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 [&_td]:bg-inherit">
                        <template
                            v-for="(domainList, group) in {
                                active: activeDomains,
                            }"
                        >
                            <tr
                                v-for="domain in domainList.slice(0, 3)"
                                :key="domain.domain"
                                class="cursor-pointer h-[72px] hover:bg-[#fafafa]"
                                @click="goToReports(domain)"
                            >
                                <td class="px-6 py-5 text-sm text-gray-700">
                                    <div class="flex items-center gap-3">
                                        <span
                                            class="text-[#2b7fff] text-[14px] leading-[20px] whitespace-nowrap overflow-hidden text-ellipsis"
                                            >{{ domain.domain }}</span
                                        >
                                    </div>
                                </td>
                                <template
                                    v-if="
                                        domain.report_30.pass > 0 ||
                                        domain.report_30.fail > 0
                                    "
                                >
                                    <td class="px-6 py-5 text-sm text-gray-700">
                                        <span
                                            v-if="
                                                domain.dmarcPolicy === 'reject'
                                            "
                                            data-slot="badge"
                                            class="w-fit focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&]:hover:bg-primary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-green-200 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 transition-[color,box-shadow] hover:bg-green-100 focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3"
                                            >Reject</span
                                        >
                                        <span
                                            v-else-if="
                                                domain.dmarcPolicy ===
                                                'quarantine'
                                            "
                                            data-slot="badge"
                                            class="w-fit focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&]:hover:bg-primary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 transition-[color,box-shadow] hover:bg-blue-100 focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3"
                                            >Quarantine</span
                                        >
                                        <span
                                            v-else
                                            data-slot="badge"
                                            class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                            >None</span
                                        >
                                    </td>
                                    <td class="px-6 py-5 text-sm text-gray-700">
                                        <span class="text-gray-900">{{
                                            new Intl.NumberFormat().format(
                                                domain.report_30.fail +
                                                    domain.report_30.pass,
                                            )
                                        }}</span>
                                    </td>
                                    <td class="px-6 py-5 text-sm text-gray-700">
                                        <div class="flex items-center gap-3">
                                            <div class="max-w-24 flex-1">
                                                <div
                                                    class="h-2 w-full rounded-full bg-gray-200"
                                                >
                                                    <div
                                                        class="h-2 rounded-full bg-green-500 transition-all duration-300"
                                                        :style="{
                                                            width: calculateCompliance(
                                                                domain.report_30
                                                                    .pass,
                                                                domain.report_30
                                                                    .fail,
                                                            ),
                                                            color: calculateColorByPercents(
                                                                calculateCompliance(
                                                                    domain
                                                                        .report_30
                                                                        .pass,
                                                                    domain
                                                                        .report_30
                                                                        .fail,
                                                                ),
                                                            ),
                                                        }"
                                                    ></div>
                                                </div>
                                            </div>
                                            <span
                                                class="text-sm font-medium text-green-600"
                                                >{{
                                                    calculateCompliance(
                                                        domain.report_30.pass,
                                                        domain.report_30.fail,
                                                    )
                                                }}</span
                                            >
                                        </div>
                                    </td>
                                </template>
                                <template v-else>
                                    <td class="px-6 py-4 text-left">
                                        <span
                                            data-slot="badge"
                                            class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                            >None</span
                                        >
                                    </td>
                                    <td class="px-6 py-4">
                                        <span class="text-gray-900">0</span>
                                    </td>
                                    <td
                                        colspan="3"
                                        class="px-6 py-4 text-left relative left-[-82px]"
                                    >
                                        <div
                                            v-if="group === 'active'"
                                            class="flex items-center justify-center gap-2 py-2 text-gray-500"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                stroke-width="2"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                class="lucide lucide-clock h-4 w-4"
                                                aria-hidden="true"
                                            >
                                                <circle
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                ></circle>
                                                <polyline
                                                    points="12 6 12 12 16 14"
                                                ></polyline></svg
                                            ><span class="text-sm text-left"
                                                >Waiting for reports</span
                                            >
                                        </div>
                                    </td>
                                </template>
                                <td
                                    class="relative text-center pr-2"
                                    @click.stop
                                >
                                    <button
                                        class="body-td-misc-button"
                                        aria-haspopup="menu"
                                        :aria-expanded="
                                            openMenuKey === domain.domain
                                                ? 'true'
                                                : 'false'
                                        "
                                        aria-label="Open actions"
                                        @click.stop="toggleMenu(domain, $event)"
                                    />

                                    <teleport to="body">
                                        <transition name="fade">
                                            <div
                                                v-if="
                                                    openMenuKey ===
                                                    domain.domain
                                                "
                                                :ref="setMenuEl"
                                                class="menu-popover"
                                                :style="menuStyle"
                                                role="menu"
                                                @click.stop
                                                @keydown.esc.stop.prevent="
                                                    closeMenu
                                                "
                                            >
                                                <button
                                                    class="menu-item"
                                                    role="menuitem"
                                                    @click.stop="
                                                        goToSettings(domain)
                                                    "
                                                >
                                                    Settings
                                                </button>
                                                <button
                                                    class="menu-item"
                                                    role="menuitem"
                                                    @click.stop="
                                                        goToReports(domain)
                                                    "
                                                >
                                                    See reports
                                                </button>
                                            </div>
                                        </transition>
                                    </teleport>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    type PropType,
    ref,
} from "vue";
import type IDomain from "@/types/Domains/IDomain";
import type IUser from "@/types/Members/IUser";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/IRhizomPageProps";
import route from "ziggy-js";
import { Mail, Shield, ShieldAlertIcon } from "lucide-vue-next";
import useNumberFormat from "@/composables/useNumberFormat.ts";
import type { IPaddleSubscription } from "@/types/Subscriptions/IPaddleSubscription.ts";
import { Link, router } from "@inertiajs/vue3";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();

const tt = computed(() => ({
    activeDomains: t("vue.active_domains_tooltip"),
    inactiveDomains: t("vue.inactive_domains_tooltip"),
    emptySlots: t("vue.empty_slots_tooltip"),
}));

const globalProps = computed(() => usePage<IRhizomPageProps>().props);
const userProps = computed(() => globalProps.value.user as IUser);

const { formatNumber } = useNumberFormat();

const domainsNoneImgSrc = new URL(
    "/resources/js/assets/images/dmarceye-app-img-domains-none.png",
    import.meta.url,
).href;

const props = defineProps({
    activeDomains: {
        type: Array as PropType<IDomain[]>,
        default: () => [],
    },
    inactiveDomains: {
        type: Array as PropType<IDomain[]>,
        default: () => [],
    },
    trialEndsAt: {
        type: String,
        default: "",
    },
    planDomains: {
        type: Number,
        default: 0,
    },
    planName: {
        type: String,
        default: "",
    },
    paymentCycle: {
        type: String,
        default: "",
    },
    nextInvoice: {
        type: String,
        default: "",
    },
    nextPayment: {
        type: String,
        default: "",
    },
    domainSlots: {
        type: Number,
        default: 0,
    },
    emptySlots: {
        type: Number,
        default: 0,
    },
    activeDomainsLength: {
        type: Number,
        default: 0,
    },
    inActiveDomainsLength: {
        type: Number,
        default: 0,
    },
    paddleSubscription: {
        type: Object as PropType<IPaddleSubscription | null>,
        default: null,
    },
    paddleCustomerPortalUrl: {
        type: String,
        default: "",
    },
});

const inertiaProps = usePage<IRhizomPageProps>();
const hasGodMode = computed(() => inertiaProps.props.hasGodMode);
const isSubscribed = computed(() => inertiaProps.props.isSubscribed);
const isTrialing = computed(() => inertiaProps.props.isTrialing);
const openMenuKey = ref<string | null>(null);
const anchorEl = ref<HTMLElement | null>(null);
const menuEl = ref<HTMLElement | null>(null);
const setMenuEl = (el: Element | null) => {
    menuEl.value = el as HTMLElement | null;
};
const menuStyle = ref<Record<string, string>>({});

const asyncPaddleSubscription = ref<IPaddleSubscription | null>(null);
const asyncPaddleCustomerPortalUrl = ref<string>("");

const currentPaddleSubscription = computed(
    () => asyncPaddleSubscription.value || props.paddleSubscription,
);

const currentPaddleCustomerPortalUrl = computed(
    () => asyncPaddleCustomerPortalUrl.value || props.paddleCustomerPortalUrl,
);

function visitCustomerPortal() {
    if (currentPaddleCustomerPortalUrl.value) {
        window.open(currentPaddleCustomerPortalUrl.value, "_blank", "noopener");
    } else {
        console.error("Paddle customer portal URL is not set.");
    }
}

const totalPass = computed(() => {
    var total = 0;

    props.activeDomains.forEach((value) => (total += value.report_30.pass));

    return total;
});

const totalFail = computed(() => {
    var total = 0;

    props.activeDomains.forEach((value) => (total += value.report_30.fail));

    return total;
});

const calculateCompliance = (pass, fail) => {
    return pass + fail > 0
        ? Math.floor((pass / (pass + fail)) * 100) + "%"
        : 0 + "%";
};
const calculateColorByPercents = (percents) => {
    var percentNum = parseInt(percents.replace("%", ""));

    if (percentNum > 75) {
        return "#00c951";
    }

    if (percentNum > 50) {
        return "#f0b100";
    }

    return "#fb2c36";
};

const avgCompliance = computed(() => {
    const passFailTotal = totalPass.value + totalFail.value;
    if (passFailTotal === 0) return 0;

    return (
        Math.round(
            (totalPass.value / (totalPass.value + totalFail.value)) * 1000,
        ) / 10
    );
});

const donutData = computed(() => {
    const compliant = avgCompliance.value;

    return [
        { key: "compliant", value: compliant },
        { key: "non-compliant", value: 100 - compliant },
    ];
});

const redirectToDomains = (e) => {
    e?.preventDefault?.();
    router.visit(route("domains.index"));
};

const goToReports = (domain: IDomain) => {
    router.get(route("reports.show", { domain: domain.domain }));
};

const goToSettings = (domain: IDomain) => {
    router.get(route("reports.show", { domain: domain.domain }) + "/settings");
};

const closeMenu = () => {
    openMenuKey.value = null;
    anchorEl.value = null;
};

const positionMenu = () => {
    if (!anchorEl.value || !menuEl.value) return;

    const padding = 8;
    const a = anchorEl.value.getBoundingClientRect();

    const m = menuEl.value.getBoundingClientRect();

    let top = a.bottom + 8;
    let left = a.right - m.width;

    if (left < padding) left = padding;
    if (left + m.width > window.innerWidth - padding)
        left = window.innerWidth - padding - m.width;

    if (top + m.height > window.innerHeight - padding) {
        top = a.top - 8 - m.height;
    }

    menuStyle.value = {
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
        right: "auto",
        bottom: "auto",
        zIndex: "9999",
    };
};

const onClickOutside = (e: MouseEvent) => {
    if (!openMenuKey.value) return;
    const t = e.target as Node;

    if (menuEl.value?.contains(t)) return;
    if (anchorEl.value?.contains(t)) return;

    closeMenu();
};

const onReflow = () => {
    if (!openMenuKey.value) return;
    positionMenu();
};

const toggleMenu = async (domain: IDomain, e: MouseEvent) => {
    const btn = e.currentTarget as HTMLElement;

    if (openMenuKey.value === domain.domain) {
        closeMenu();
        return;
    }

    openMenuKey.value = domain.domain;
    anchorEl.value = btn;

    await nextTick();
    positionMenu();
};
onMounted(() => {
    document.addEventListener("click", onClickOutside);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", onClickOutside);
    window.removeEventListener("resize", onReflow);
    window.removeEventListener("scroll", onReflow, true);
});
</script>

<style scoped>
/*DOMAINS NONE LIST*/
.domains-none-list {
    display: flex;
    width: 539px;
    flex-direction: column;
    align-items: center;
    margin: auto;
    gap: 41px;
}

.domains-none-image {
    width: 539px;
    height: 244px;
    aspect-ratio: 539/244;
}

.domains-none-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 9px;
}

.domains-none-button {
    display: flex;
    width: 135px;
    flex-direction: column;
    align-items: center;
    gap: 10px;
}

.domain-non-text-header {
    color: #000;
    text-align: center;
    font-size: 35px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
}

.domain-non-text-header-paragraph {
    color: #18181b;
    text-align: center;
    font-size: 16px;
    font-style: normal;
    width: 539px;
    font-weight: 400;
    line-height: 29px;
    letter-spacing: -0.18px;
}

/*KEBAB MENU*/
.body-td-misc-button {
    display: inline-flex;
    width: 28px;
    height: 28px;
    justify-content: center;
    align-items: center;
    border-radius: 6px;
    border: 1px solid #d2d6dc;
    background: #f3f5f8;
    cursor: pointer;
    line-height: 1;
}
.body-td-misc-button::before {
    content: "⋮";
    font-size: 16px;
}

.menu-popover {
    z-index: 9999;
    width: 176px;
    border: 1px solid #e5e7eb;
    background: #fff;
    border-radius: 8px;
    box-shadow:
        0 8px 24px rgba(16, 24, 40, 0.12),
        0 2px 8px rgba(16, 24, 40, 0.06);
    padding: 6px 0;
}
.menu-popover:hover,
.menu-item:hover {
    cursor: pointer;
}
</style>
